import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MessageCircle, HelpCircle, BookOpen, Zap } from 'lucide-react';

const SupportPage = () => {
  const navigate = useNavigate();
  
  // Import Google Font Caveat
  React.useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  const faqs = [
    {
      question: "Comment créer un profil enfant ?",
      answer: "Depuis l'application, cliquez sur 'Ajouter un enfant' et renseignez les informations demandées (prénom, âge, genre). Vous pourrez ensuite personnaliser l'expérience pour chaque enfant."
    },
    {
      question: "Combien d'histoires puis-je générer ?",
      answer: "Le nombre d'histoires dépend de votre abonnement. Consultez la page 'Abonnement' pour connaître votre quota mensuel et les options disponibles."
    },
    {
      question: "Comment fonctionne la génération d'histoires ?",
      answer: "Notre IA crée des histoires personnalisées en fonction de l'âge de votre enfant, du thème choisi et de la durée souhaitée. Chaque histoire est unique et illustrée."
    },
    {
      question: "Puis-je annuler mon abonnement ?",
      answer: "Oui, vous pouvez annuler votre abonnement à tout moment depuis la page 'Abonnement'. Vous conserverez l'accès jusqu'à la fin de la période payée."
    },
    {
      question: "Les données de mes enfants sont-elles sécurisées ?",
      answer: "Absolument. Nous ne partageons jamais vos données personnelles avec des tiers. Consultez notre politique de confidentialité pour plus de détails."
    }
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 hover:bg-gray-100 rounded-full transition-colors mr-4"
            data-testid="support-back-btn"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: '"Caveat", cursive' }}>
            Support & Aide
          </h1>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Contact Section */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 mb-8 shadow-lg" data-testid="support-contact-section">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <MessageCircle className="w-6 h-6 mr-2 text-teal-500" />
            Nous contacter
          </h2>
          <p className="text-gray-600 mb-4">
            Vous avez une question ou besoin d'aide ? N'hésitez pas à nous contacter !
          </p>
          <a 
            href="mailto:contact@dismaman.com"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-xl font-medium hover:from-blue-600 hover:to-teal-600 transition-all duration-200 shadow-md"
            data-testid="support-email-btn"
          >
            <Mail className="w-5 h-5 mr-2" />
            contact@dismaman.com
          </a>
        </div>

        {/* FAQ Section */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 mb-8 shadow-lg" data-testid="support-faq-section">
          <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
            <HelpCircle className="w-6 h-6 mr-2 text-teal-500" />
            Questions fréquentes
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details 
                key={index} 
                className="group bg-gray-50 rounded-xl overflow-hidden"
                data-testid={`faq-item-${index}`}
              >
                <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100 transition-colors">
                  <span className="font-medium text-gray-800">{faq.question}</span>
                  <span className="text-teal-500 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-4 pb-4 text-gray-600">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>

        {/* Quick Tips */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg" data-testid="support-tips-section">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <Zap className="w-6 h-6 mr-2 text-amber-500" />
            Astuces rapides
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-xl">
              <BookOpen className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-800">Histoires personnalisées</p>
                <p className="text-sm text-gray-600">Incluez le nom de votre enfant dans l'histoire pour plus d'immersion !</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-xl">
              <Zap className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-800">Mode conversation</p>
                <p className="text-sm text-gray-600">Posez des questions à l'IA pour explorer la curiosité de vos enfants.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Version info */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>Dis Maman! - Version 1.0</p>
          <p>© 2024 Dis Maman! - Tous droits réservés</p>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
